import _slicedToArray from "/home/hyuto/Hyuto.github.io/v3-beta/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _asyncToGenerator from "/home/hyuto/Hyuto.github.io/v3-beta/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _classCallCheck from "/home/hyuto/Hyuto.github.io/v3-beta/node_modules/@babel/runtime/helpers/esm/classCallCheck.js";
import _createClass from "/home/hyuto/Hyuto.github.io/v3-beta/node_modules/@babel/runtime/helpers/esm/createClass.js";
import _regeneratorRuntime from "/home/hyuto/Hyuto.github.io/v3-beta/node_modules/@babel/runtime/regenerator/index.js";

/**
 * @license
 * Copyright 2017 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * =============================================================================
 */
import { isPromise } from './util_base'; // Expects flags from URL in the format ?tfjsflags=FLAG1:1,FLAG2:true.

var TENSORFLOWJS_FLAGS_PREFIX = 'tfjsflags';
/**
 * The environment contains evaluated flags as well as the registered platform.
 * This is always used as a global singleton and can be retrieved with
 * `tf.env()`.
 *
 * @doc {heading: 'Environment'}
 */

export var Environment = /*#__PURE__*/function () {
  // tslint:disable-next-line: no-any
  function Environment(global) {
    _classCallCheck(this, Environment);

    this.global = global;
    this.flags = {};
    this.flagRegistry = {};
    this.urlFlags = {}; // Jasmine spies on this in 'environment_test.ts'

    this.getQueryParams = getQueryParams;
    this.populateURLFlags();
  }

  _createClass(Environment, [{
    key: "setPlatform",
    value: function setPlatform(platformName, platform) {
      if (this.platform != null) {
        if (!(env().getBool('IS_TEST') || env().getBool('PROD'))) {
          console.warn("Platform ".concat(this.platformName, " has already been set. ") + "Overwriting the platform with ".concat(platformName, "."));
        }
      }

      this.platformName = platformName;
      this.platform = platform;
    }
  }, {
    key: "registerFlag",
    value: function registerFlag(flagName, evaluationFn, setHook) {
      this.flagRegistry[flagName] = {
        evaluationFn: evaluationFn,
        setHook: setHook
      }; // Override the flag value from the URL. This has to happen here because
      // the environment is initialized before flags get registered.

      if (this.urlFlags[flagName] != null) {
        var flagValue = this.urlFlags[flagName];

        if (!(env().getBool('IS_TEST') || env().getBool('PROD'))) {
          console.warn("Setting feature override from URL ".concat(flagName, ": ").concat(flagValue, "."));
        }

        this.set(flagName, flagValue);
      }
    }
  }, {
    key: "getAsync",
    value: function () {
      var _getAsync = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(flagName) {
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                if (!(flagName in this.flags)) {
                  _context.next = 2;
                  break;
                }

                return _context.abrupt("return", this.flags[flagName]);

              case 2:
                _context.next = 4;
                return this.evaluateFlag(flagName);

              case 4:
                this.flags[flagName] = _context.sent;
                return _context.abrupt("return", this.flags[flagName]);

              case 6:
              case "end":
                return _context.stop();
            }
          }
        }, _callee, this);
      }));

      function getAsync(_x) {
        return _getAsync.apply(this, arguments);
      }

      return getAsync;
    }()
  }, {
    key: "get",
    value: function get(flagName) {
      if (flagName in this.flags) {
        return this.flags[flagName];
      }

      var flagValue = this.evaluateFlag(flagName);

      if (isPromise(flagValue)) {
        throw new Error("Flag ".concat(flagName, " cannot be synchronously evaluated. ") + "Please use getAsync() instead.");
      }

      this.flags[flagName] = flagValue;
      return this.flags[flagName];
    }
  }, {
    key: "getNumber",
    value: function getNumber(flagName) {
      return this.get(flagName);
    }
  }, {
    key: "getBool",
    value: function getBool(flagName) {
      return this.get(flagName);
    }
  }, {
    key: "getFlags",
    value: function getFlags() {
      return this.flags;
    } // For backwards compatibility.

  }, {
    key: "features",
    get: function get() {
      return this.flags;
    }
  }, {
    key: "set",
    value: function set(flagName, value) {
      if (this.flagRegistry[flagName] == null) {
        throw new Error("Cannot set flag ".concat(flagName, " as it has not been registered."));
      }

      this.flags[flagName] = value;

      if (this.flagRegistry[flagName].setHook != null) {
        this.flagRegistry[flagName].setHook(value);
      }
    }
  }, {
    key: "evaluateFlag",
    value: function evaluateFlag(flagName) {
      if (this.flagRegistry[flagName] == null) {
        throw new Error("Cannot evaluate flag '".concat(flagName, "': no evaluation function found."));
      }

      return this.flagRegistry[flagName].evaluationFn();
    }
  }, {
    key: "setFlags",
    value: function setFlags(flags) {
      this.flags = Object.assign({}, flags);
    }
  }, {
    key: "reset",
    value: function reset() {
      this.flags = {};
      this.urlFlags = {};
      this.populateURLFlags();
    }
  }, {
    key: "populateURLFlags",
    value: function populateURLFlags() {
      var _this = this;

      if (typeof this.global === 'undefined' || typeof this.global.location === 'undefined' || typeof this.global.location.search === 'undefined') {
        return;
      }

      var urlParams = this.getQueryParams(this.global.location.search);

      if (TENSORFLOWJS_FLAGS_PREFIX in urlParams) {
        var keyValues = urlParams[TENSORFLOWJS_FLAGS_PREFIX].split(',');
        keyValues.forEach(function (keyValue) {
          var _keyValue$split = keyValue.split(':'),
              _keyValue$split2 = _slicedToArray(_keyValue$split, 2),
              key = _keyValue$split2[0],
              value = _keyValue$split2[1];

          _this.urlFlags[key] = parseValue(key, value);
        });
      }
    }
  }]);

  return Environment;
}();
export function getQueryParams(queryString) {
  var params = {};
  queryString.replace(/[?&]([^=?&]+)(?:=([^&]*))?/g, function (s) {
    for (var _len = arguments.length, t = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      t[_key - 1] = arguments[_key];
    }

    decodeParam(params, t[0], t[1]);
    return t.join('=');
  });
  return params;
}

function decodeParam(params, name, value) {
  params[decodeURIComponent(name)] = decodeURIComponent(value || '');
}

function parseValue(flagName, value) {
  value = value.toLowerCase();

  if (value === 'true' || value === 'false') {
    return value === 'true';
  } else if ("".concat(+value) === value) {
    return +value;
  }

  throw new Error("Could not parse value flag value ".concat(value, " for flag ").concat(flagName, "."));
}
/**
 * Returns the current environment (a global singleton).
 *
 * The environment object contains the evaluated feature values as well as the
 * active platform.
 *
 * @doc {heading: 'Environment'}
 */


export function env() {
  return ENV;
}
export var ENV = null;
export function setEnvironmentGlobal(environment) {
  ENV = environment;
}